import * as Comlink from 'comlink';
/*
 * AiClient — the main-thread handle to the AI web worker (the SECOND worker, alongside the Backend
 * Worker). Spawns it, wraps it with Comlink, and owns the identical session-JWT plumbing the Backend
 * Worker uses: the main thread holds the session and pushes the token in, refreshing it ahead
 * of its ~60s expiry. Deliberately a twin of bridge/backend-client.ts — one boundary, one
 * token-refresh contract, so the AI plane authenticates exactly like the data plane.
 */
/*
 * litSafe makes a Comlink Remote bindable in a lit template.
 *
 * lit probes EVERY bound value for `_$litDirective$` to decide whether it is a
 * directive. A Comlink proxy answers every property access with another truthy
 * proxy, so the probe "succeeds": lit treats the whole API handle as a
 * directive, calls `new` on the fake class, and Comlink dutifully posts the
 * construct — carrying lit's un-cloneable `nothing` sentinel — into the worker.
 * The visible wreckage is a white page throwing `currentDirective._$initialize
 * is not a function` + `DataCloneError: Symbol(lit-nothing) could not be
 * cloned` in a loop, which reads exactly like two lit instances and is not:
 * one lit, one proxy that cannot say "no".
 *
 * So this one key answers undefined, here, at the single place the Remote is
 * born — rather than at every `.api=${...}` binding, where the next component
 * to take the handle as a property would re-import the crash.
 */
export const litSafe = (remote) => new Proxy(remote, {
    get: (t, k, r) => (k === '_$litDirective$' ? undefined : Reflect.get(t, k, r)),
});
export class AiClient {
    constructor(api, worker, getToken, refreshTimer, onVisibilityChange) {
        this.api = api;
        this.worker = worker;
        this.getToken = getToken;
        this.refreshTimer = refreshTimer;
        this.onVisibilityChange = onVisibilityChange;
    }
    static async create(
    /** Created by the APP, not here: a bundler resolves `new Worker(new URL('./x.ts', import.meta.url))`
     *  only from a literal in the app's own source, so the worker entry stays per-app while its
     *  implementation (`createAiApi`) is shared. */
    worker, getToken, 
    /** Force-fresh token for on-demand recovery after a stale-JWT 401 — same
     *  rationale as BackendClient (re-reading the cache returns the same dead token). */
    getFreshToken = getToken) {
        const api = litSafe(Comlink.wrap(worker));
        await api.init(await getToken(), Comlink.proxy(getFreshToken));
        const refreshTimer = window.setInterval(() => {
            void (async () => api.setToken(await getToken()))();
        }, 50_000);
        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible')
                void (async () => api.setToken(await getToken()))();
        };
        document.addEventListener('visibilitychange', onVisibilityChange);
        return new AiClient(api, worker, getToken, refreshTimer, onVisibilityChange);
    }
    /** Push the current JWT into the worker now (e.g. right after sign-in). */
    async refreshToken() {
        await this.api.setToken(await this.getToken());
    }
    dispose() {
        window.clearInterval(this.refreshTimer);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        this.worker.terminate();
    }
}
//# sourceMappingURL=client.js.map